import { X, Printer, FileDown } from "lucide-react";
import { useRef } from "react";
import ThermalReport from "./ThermalReport";
import { toCSV, downloadCSV, brMoney, formatDateBR } from "../lib/csv";

export default function DetalheVendasModal({
  open,
  onClose,
  titulo,
  escopo,
  periodo,
  vendas,
  metricas,
}) {
  const ref = useRef(null);
  if (!open) return null;

  const exportCSV = () => {
    const rows = [];
    vendas.forEach((v) => {
      if (!v.itens || v.itens.length === 0) {
        rows.push({ ...v, item: null });
      } else {
        v.itens.forEach((it) => rows.push({ ...v, item: it }));
      }
    });
    const headers = [
      { label: "ID Venda", get: (r) => r.id },
      { label: "Data", get: (r) => formatDateBR(r.data_venda) },
      { label: "Unidade", get: (r) => r.unidade_nome || r.unidade_id },
      { label: "Vendedor", get: (r) => r.vendedor_nome },
      { label: "Cliente", get: (r) => r.cliente_nome || "" },
      { label: "Placa", get: (r) => r.placa || "" },
      { label: "KM", get: (r) => r.km_atual || "" },
      { label: "Produto", get: (r) => r.item?.nome || "" },
      { label: "Qtd", get: (r) => r.item?.quantidade || "" },
      { label: "Preço Unit", get: (r) => r.item ? r.item.preco_unitario.toFixed(2).replace(".", ",") : "" },
      { label: "Subtotal Item", get: (r) => r.item ? r.item.subtotal.toFixed(2).replace(".", ",") : "" },
      { label: "Total Bruto", get: (r) => r.total_bruto.toFixed(2).replace(".", ",") },
      { label: "% Desconto", get: (r) => r.percentual_desconto.toFixed(2).replace(".", ",") },
      { label: "Desconto R$", get: (r) => r.total_desconto.toFixed(2).replace(".", ",") },
      { label: "Total Líquido", get: (r) => r.total_liquido.toFixed(2).replace(".", ",") },
      { label: "CMV", get: (r) => r.total_custo_cmv.toFixed(2).replace(".", ",") },
      { label: "Lucro", get: (r) => r.lucro.toFixed(2).replace(".", ",") },
    ];
    const csv = toCSV(rows, headers);
    const now = new Date().toISOString().slice(0, 10);
    downloadCSV(`relatorio_${now}.csv`, csv);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white border-4 border-neutral-900 w-full max-w-4xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b-2 border-neutral-900 print:hidden">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
              Relatório detalhado
            </p>
            <h3 className="font-display font-black text-xl tracking-tight">{titulo}</h3>
            {(escopo || periodo) && (
              <p className="font-mono text-xs text-neutral-600 mt-1">
                {escopo && <>ESCOPO: {escopo}</>} {periodo && <>· PERÍODO: {periodo}</>}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              data-testid="relatorio-csv-btn"
              onClick={exportCSV}
              className="bg-neutral-950 text-white h-10 px-4 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2"
            >
              <FileDown className="w-4 h-4" /> CSV
            </button>
            <button
              data-testid="relatorio-imprimir-btn"
              onClick={() => window.print()}
              className="bg-yellow-400 text-neutral-950 h-10 px-4 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2"
            >
              <Printer className="w-4 h-4" /> Térmico
            </button>
            <button
              data-testid="relatorio-fechar-btn"
              onClick={onClose}
              className="p-2 border-2 border-neutral-900 hover:bg-neutral-900 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-4 print:hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-neutral-900 bg-neutral-50">
                  <th className="text-left p-2 font-mono text-[10px] uppercase tracking-widest">Data</th>
                  <th className="text-left p-2 font-mono text-[10px] uppercase tracking-widest">Unidade</th>
                  <th className="text-left p-2 font-mono text-[10px] uppercase tracking-widest">Vendedor</th>
                  <th className="text-left p-2 font-mono text-[10px] uppercase tracking-widest">Cliente</th>
                  <th className="text-left p-2 font-mono text-[10px] uppercase tracking-widest">Placa</th>
                  <th className="text-right p-2 font-mono text-[10px] uppercase tracking-widest">Itens</th>
                  <th className="text-right p-2 font-mono text-[10px] uppercase tracking-widest">Bruto</th>
                  <th className="text-right p-2 font-mono text-[10px] uppercase tracking-widest">Desc</th>
                  <th className="text-right p-2 font-mono text-[10px] uppercase tracking-widest">Líquido</th>
                  <th className="text-right p-2 font-mono text-[10px] uppercase tracking-widest">Lucro</th>
                </tr>
              </thead>
              <tbody>
                {vendas.map((v) => (
                  <tr key={v.id} className="border-b border-neutral-100 align-top">
                    <td className="p-2 font-mono text-xs">{formatDateBR(v.data_venda)}</td>
                    <td className="p-2 text-xs">{v.unidade_nome || "—"}</td>
                    <td className="p-2 text-xs font-bold">{v.vendedor_nome}</td>
                    <td className="p-2 text-xs">{v.cliente_nome || "—"}</td>
                    <td className="p-2 font-mono text-xs">{v.placa || "—"}</td>
                    <td className="p-2 text-right font-mono text-xs">
                      <div className="font-bold">
                        {v.itens?.reduce((s, i) => s + i.quantidade, 0) || 0}
                      </div>
                      {v.itens?.map((it, i) => (
                        <div key={i} className="text-[10px] text-neutral-500">
                          {it.quantidade}x {it.nome.slice(0, 20)}
                        </div>
                      ))}
                    </td>
                    <td className="p-2 text-right font-mono text-xs">
                      {brMoney(v.total_bruto)}
                    </td>
                    <td className="p-2 text-right font-mono text-xs text-red-600">
                      - {brMoney(v.total_desconto)}
                    </td>
                    <td className="p-2 text-right font-mono text-xs font-bold">
                      {brMoney(v.total_liquido)}
                    </td>
                    <td className="p-2 text-right font-mono text-xs text-green-700">
                      {brMoney(v.lucro)}
                    </td>
                  </tr>
                ))}
                {vendas.length === 0 && (
                  <tr>
                    <td colSpan={10} className="p-8 text-center text-sm text-neutral-500">
                      Nenhuma venda no escopo
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Versão térmica invisível em tela (só aparece ao imprimir) */}
        <div className="hidden print:block" ref={ref}>
          <ThermalReport
            titulo={titulo}
            periodo={periodo}
            escopo={escopo}
            metricas={metricas}
            vendas={vendas}
          />
        </div>
      </div>
    </div>
  );
}
